import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../store/Index";
import CustomTextField from "../../../../../../../../components/common/Textfield";

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  selectedServices: {
    videoDownload: boolean;
    memoryBook: boolean;
  };
  total: number;
}

type PaymentMethod = "visa" | "ecocash" | "paypal" | "other";

function CheckoutModal({
  open,
  onClose,
  selectedServices,
  total,
}: CheckoutModalProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("visa");
  const [formData, setFormData] = useState({
    nameOnCard: "",
    cardNumber: "",
    year: "",
    cvc: "",
    nameOnAccount: "",
    accountNumber: "",
    ecocashNumber: "",
  });

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePurchase = () => {
    // TODO: Handle purchase logic here
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          borderBottom: 1,
          borderColor: "grey.300",
        }}
      >
        <Typography variant="h6" component="div">
          Checkout
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Order Section */}
              <Box>
                <Typography
                  sx={{ fontWeight: "bold", fontSize: "14px", mb: 2 }}
                >
                  Your Order
                </Typography>

                <Box sx={{ mb: 3 }}>
                  {selectedServices.memoryBook && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "13px" }}>
                        Memory Book
                      </Typography>
                      <Typography sx={{ fontSize: "13px" }}>$5.00</Typography>
                    </Box>
                  )}
                  {selectedServices.videoDownload && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ fontSize: "13px" }}>
                        Download link
                      </Typography>
                      <Typography sx={{ fontSize: "13px" }}>$25.00</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

               
              <Box sx={{ mt: "auto" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1,
                    borderColor: "grey.300",
                  }}
                >
                  <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
                    Total
                  </Typography>
                  <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
                    ${total}.00
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          
          <Grid size={{ xs: 12, md: 7 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Payment Details Section */}
              <Box>
                <Typography
                  sx={{ fontWeight: "bold", fontSize: "14px", mb: 2 }}
                >
                  Payment Details
                </Typography>

                <Typography
                  sx={{ fontSize: "12px", mb: 1, color: "text.secondary" }}
                >
                  Select Payment method
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={() => setPaymentMethod("visa")}
                    sx={{
                      textTransform: "none",
                      p: 1.5,
                      px: 2,
                      borderRadius: 2,
                      backgroundColor:
                        paymentMethod === "visa" ? "grey.400" : "grey.200",
                      color:
                        paymentMethod === "visa" ? "white" : "text.primary",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor:
                          paymentMethod === "visa" ? "grey.500" : "grey.300",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Visa
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setPaymentMethod("ecocash")}
                    sx={{
                      textTransform: "none",
                      p: 1.5,
                      px: 2,
                      borderRadius: 2,
                      backgroundColor:
                        paymentMethod === "ecocash" ? "grey.400" : "grey.200",
                      color:
                        paymentMethod === "ecocash" ? "white" : "text.primary",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor:
                          paymentMethod === "ecocash" ? "grey.500" : "grey.300",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Ecocash
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setPaymentMethod("paypal")}
                    sx={{
                      textTransform: "none",
                      p: 1.5,
                      px: 2,
                      borderRadius: 2,
                      backgroundColor:
                        paymentMethod === "paypal" ? "grey.400" : "grey.200",
                      color:
                        paymentMethod === "paypal" ? "white" : "text.primary",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor:
                          paymentMethod === "paypal" ? "grey.500" : "grey.300",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Paypal
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setPaymentMethod("other")}
                    disabled={true}
                    sx={{
                      textTransform: "none",
                      p: 1.5,
                      px: 2,
                      borderRadius: 2,
                      backgroundColor:
                        paymentMethod === "other" ? "grey.400" : "grey.200",
                      color:
                        paymentMethod === "other" ? "white" : "text.primary",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor:
                          paymentMethod === "other" ? "grey.500" : "grey.300",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Other
                  </Button>
                </Box>

                {/* Form Based on Payment Method */}
                {paymentMethod === "visa" && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <CustomTextField
                      fullWidth
                      label="Name on card"
                      variant="outlined"
                      value={formData.nameOnCard}
                      onChange={(e) =>
                        handleInputChange("nameOnCard", e.target.value)
                      }
                      size="small"
                    />
                    <CustomTextField
                      fullWidth
                      label="Card Number"
                      variant="outlined"
                      value={formData.cardNumber}
                      onChange={(e) =>
                        handleInputChange("cardNumber", e.target.value)
                      }
                      size="small"
                    />
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <CustomTextField
                        fullWidth
                        label="Year"
                        variant="outlined"
                        value={formData.year}
                        onChange={(e) =>
                          handleInputChange("year", e.target.value)
                        }
                        size="small"
                      />
                      <CustomTextField
                        fullWidth
                        label="CVC"
                        variant="outlined"
                        value={formData.cvc}
                        onChange={(e) =>
                          handleInputChange("cvc", e.target.value)
                        }
                        size="small"
                      />
                    </Box>
                  </Box>
                )}

                {paymentMethod === "ecocash" && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <CustomTextField
                      fullWidth
                      label="Name on Account"
                      variant="outlined"
                      value={formData.nameOnAccount}
                      onChange={(e) =>
                        handleInputChange("nameOnAccount", e.target.value)
                      }
                      size="small"
                    />
                    <CustomTextField
                      fullWidth
                      label="Ecocash Number"
                      variant="outlined"
                      value={formData.ecocashNumber}
                      onChange={(e) =>
                        handleInputChange("ecocashNumber", e.target.value)
                      }
                      size="small"
                    />
                  </Box>
                )}

                {paymentMethod === "paypal" && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <CustomTextField
                      fullWidth
                      label="Name on Account"
                      variant="outlined"
                      value={formData.nameOnAccount}
                      onChange={(e) =>
                        handleInputChange("nameOnAccount", e.target.value)
                      }
                      size="small"
                    />
                    <CustomTextField
                      fullWidth
                      label="Account Number"
                      variant="outlined"
                      value={formData.accountNumber}
                      onChange={(e) =>
                        handleInputChange("accountNumber", e.target.value)
                      }
                      size="small"
                    />
                  </Box>
                )}
              </Box>

              {/* Buttons  */}
              <Box sx={{ mt: "auto", pt: 3, }}>
                <DialogActions sx={{ p: 0 }}>
                  {/* <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      borderColor: reduxTheme.primary_colors[400],
                      color: reduxTheme.primary_colors[400],
                      mr: 2,
                      flex:1,
                    }}
                  >
                    Cancel
                  </Button> */}
                  <Button
                    variant="contained"
                    onClick={handlePurchase}
                    sx={{
                      textTransform: "none",
                      backgroundColor: reduxTheme.primary_colors[400],
                      flex:1,
                      py:1,
                    }}
                  >
                    Purchase
                  </Button>
                </DialogActions>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default CheckoutModal;
